import React from 'react';
import { theme, commonStyles } from '../styles';
import { personalInfo, experiences, education, softSkills, interests } from '../data';

const AboutPage = ({ isMobile }) => {
  return (
    <section style={{
      ...commonStyles.page,
      ...(isMobile ? commonStyles.pageMobile : {}),
    }}>
      <h1 style={{
        ...commonStyles.pageTitle,
        ...(isMobile ? commonStyles.pageTitleMobile : {}),
      }}>À Propos</h1>
      
      {/* Info Cards */}
      <div style={{
        ...styles.aboutGrid,
        gap: isMobile ? '1rem' : '2rem',
      }}>
        {/* Profil Card */}
        <div style={{
          ...commonStyles.card,
          ...(isMobile ? commonStyles.cardMobile : {}),
        }}>
          <h2 style={commonStyles.cardTitle}>Profil</h2>
          <p style={{
            ...styles.aboutText,
            fontSize: isMobile ? '0.9rem' : '1rem',
          }}>
            {personalInfo.aboutBio}
          </p>
          <div style={styles.infoList}>
            <InfoItem label="Âge" value={`${personalInfo.age} ans`} />
            <InfoItem 
              label="Localisation" 
              value={personalInfo.location}
              isMobile={isMobile}
            />
            <InfoItem label="Permis" value={personalInfo.license} />
            <InfoItem label="Anglais" value={personalInfo.englishLevel} />
          </div>
        </div>

        {/* Soft Skills Card */}
        <div style={{
          ...commonStyles.card,
          ...(isMobile ? commonStyles.cardMobile : {}),
        }}>
          <h2 style={commonStyles.cardTitle}>Soft Skills</h2>
          <div style={styles.softSkills}>
            {softSkills.map((skill) => (
              <span 
                key={skill} 
                style={{
                  ...commonStyles.tag,
                  ...(isMobile ? commonStyles.tagMobile : {}),
                }}
              >
                {skill}
              </span>
            ))}
          </div>
          
          <h2 style={{
            ...commonStyles.cardTitle, 
            marginTop: isMobile ? '1.5rem' : '2rem'
          }}>
            Centres d'Intérêt
          </h2>
          <div style={styles.interests}>
            {interests.map((interest, index) => (
              <div key={index} style={styles.interest}>
                <span style={styles.interestTitle}>{interest.title}</span>
                <span style={{
                  ...styles.interestDesc,
                  fontSize: isMobile ? '0.8rem' : '0.9rem',
                }}>
                  {interest.description}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Expériences */}
      <h2 style={{
        ...commonStyles.sectionTitle,
        ...(isMobile ? commonStyles.sectionTitleMobile : {}),
      }}>
        Expériences Professionnelles
      </h2>
      <div style={{
        ...styles.timeline,
        paddingLeft: isMobile ? '1rem' : '2rem',
      }}>
        {experiences.map((exp, index) => (
          <TimelineItem 
            key={index} 
            experience={exp} 
            isMobile={isMobile} 
          />
        ))}
      </div>

      {/* Formations */}
      <h2 style={{
        ...commonStyles.sectionTitle,
        ...(isMobile ? commonStyles.sectionTitleMobile : {}),
      }}>
        Formations
      </h2>
      <div style={{
        ...styles.educationGrid,
        gap: isMobile ? '1rem' : '1.5rem',
      }}>
        {education.map((edu, index) => (
          <EducationCard key={index} education={edu} isMobile={isMobile} />
        ))}
      </div>
    </section>
  );
};

// Sous-composants
const InfoItem = ({ label, value, isMobile }) => (
  <div style={styles.infoItem}>
    <span style={styles.infoLabel}>{label}</span>
    <span style={{
      ...styles.infoValue,
      fontSize: isMobile ? '0.85rem' : '1rem',
    }}>
      {value}
    </span>
  </div>
);

const TimelineItem = ({ experience, isMobile }) => (
  <div style={{
    ...styles.timelineItem,
    paddingLeft: isMobile ? '1rem' : '1.5rem',
    paddingBottom: isMobile ? '1.5rem' : '2rem',
  }}>
    <div style={{
      ...styles.timelineDot,
      width: isMobile ? '12px' : '16px',
      height: isMobile ? '12px' : '16px',
      left: isMobile ? '-7px' : '-9px',
    }} />
    <div style={{
      ...styles.timelineContent,
      padding: isMobile ? '1rem' : '1.5rem',
    }}>
      <div style={{
        ...styles.timelineHeader,
        flexDirection: isMobile ? 'column' : 'row',
      }}>
        <h3 style={{
          ...styles.timelineTitle,
          fontSize: isMobile ? '0.95rem' : '1.1rem',
        }}>
          {experience.title}
        </h3>
        <span style={{
          ...styles.timelinePeriod,
          fontSize: isMobile ? '0.75rem' : '0.85rem',
        }}>
          {experience.period}
        </span>
      </div>
      <p style={{
        ...styles.timelineCompany,
        fontSize: isMobile ? '0.85rem' : '1rem',
      }}>
        {experience.company} - {experience.location}
      </p>
      <ul style={{
        ...styles.timelineTasks,
        fontSize: isMobile ? '0.8rem' : '0.9rem',
        paddingLeft: isMobile ? '1rem' : '1.25rem',
      }}>
        {experience.tasks.map((task, i) => (
          <li key={i} style={styles.timelineTask}>{task}</li>
        ))}
      </ul>
    </div>
  </div>
);

const EducationCard = ({ education, isMobile }) => (
  <div style={{
    ...styles.educationCard,
    padding: isMobile ? '1rem' : '1.5rem',
  }}>
    <h3 style={{
      ...styles.eduDegree,
      fontSize: isMobile ? '0.95rem' : '1.1rem',
    }}>
      {education.degree}
    </h3>
    <p style={{
      ...styles.eduSchool,
      fontSize: isMobile ? '0.85rem' : '1rem',
    }}>
      {education.school}
    </p>
    <p style={styles.eduLocation}>{education.location}</p>
    <span style={styles.eduPeriod}>{education.period}</span>
  </div>
);

const styles = {
  aboutGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    marginBottom: '2rem',
  },
  aboutText: {
    color: theme.colors.textMuted,
    lineHeight: '1.8',
    marginBottom: '1.5rem',
  },
  infoList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  infoItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  infoLabel: {
    color: theme.colors.textDark,
    fontSize: '0.9rem',
  },
  infoValue: {
    color: theme.colors.text,
    fontWeight: '500',
  },
  softSkills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  interests: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  interest: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  interestTitle: {
    color: theme.colors.text,
    fontWeight: '500',
  },
  interestDesc: {
    color: theme.colors.textDark,
  },
  
  // Timeline
  timeline: {
    position: 'relative',
  },
  timelineItem: {
    position: 'relative',
    borderLeft: '2px solid rgba(0, 217, 255, 0.3)',
  },
  timelineDot: {
    position: 'absolute',
    top: '0',
    borderRadius: '50%',
    background: theme.colors.primary,
    border: `3px solid ${theme.colors.background}`,
  },
  timelineContent: {
    background: 'rgba(26, 31, 46, 0.6)',
    borderRadius: '12px',
    border: '1px solid rgba(0, 217, 255, 0.1)',
  },
  timelineHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '0.25rem',
    marginBottom: '0.5rem',
  },
  timelineTitle: {
    fontWeight: '600',
    color: theme.colors.text,
  },
  timelinePeriod: {
    color: theme.colors.primary,
    fontFamily: theme.fonts.mono,
  },
  timelineCompany: {
    color: theme.colors.textMuted,
    marginBottom: '1rem',
  },
  timelineTasks: {
    margin: 0,
    color: theme.colors.textDark,
    lineHeight: '1.8',
  },
  timelineTask: {
    marginBottom: '0.25rem',
  },
  
  // Education
  educationGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
  },
  educationCard: {
    background: 'rgba(26, 31, 46, 0.6)',
    borderRadius: '12px',
    border: '1px solid rgba(0, 217, 255, 0.1)',
    transition: theme.transition.normal,
  },
  eduDegree: {
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: '0.5rem',
  },
  eduSchool: {
    color: theme.colors.primary,
    marginBottom: '0.25rem',
  },
  eduLocation: {
    color: theme.colors.textDark,
    fontSize: '0.9rem',
    marginBottom: '0.5rem',
  },
  eduPeriod: {
    fontSize: '0.85rem',
    color: theme.colors.textMuted,
    fontFamily: theme.fonts.mono,
  },
};

export default AboutPage;
